import React, { useState, useCallback, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { SITE_CONFIG } from '../../constants/siteConfig.js';
import { MP_SURF } from '../constants/surfaces.js';
import { invokeReportError } from '../../lib/reportErrorClient.js';
import { useFiltersDrawerFab } from '../context/FiltersDrawerFabContext.js';

const CATEGORIAS = [
  {
    id: 'error',
    label: 'Error',
    tipo: 'user_report',
    ayuda: 'Reportar error para ser revisado por el equipo DreamVinyl.',
    pregunta: '¿Qué estabas haciendo?',
    placeholder: 'Ej.: intenté publicar un disco y el botón no respondió…',
    enviado: 'Error enviado. Gracias: lo revisamos en soporte.',
  },
  {
    id: 'sugerencia',
    label: 'Sugerencia',
    tipo: 'user_suggestion',
    ayuda: 'Enviar sugerencia para ser revisada por el equipo DreamVinyl.',
    pregunta: '¿Qué te gustaría mejorar?',
    placeholder: 'Ej.: sería útil poder filtrar por sello discográfico en el mercado…',
    enviado: 'Sugerencia enviada. Gracias: la revisamos con el equipo.',
  },
];

/**
 * Botón flotante (esquina inferior izquierda) + modal para errores o sugerencias.
 * `data-guest-safe`: fuera del área que fuerza registro a invitados en el marketplace home.
 */
export default function ReportarErrorBtn() {
  const { drawerOpen: filtersDrawerOpen } = useFiltersDrawerFab();
  const [open, setOpen] = useState(false);
  const [categoria, setCategoria] = useState('error');
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const cat = useMemo(() => CATEGORIAS.find(c => c.id === categoria) || CATEGORIAS[0], [categoria]);

  const pagina = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search || ''}` : '';

  const enviar = useCallback(async () => {
    const body = texto.trim();
    if (body.length < 8) {
      setMsg('Escribe un poco más de detalle (mín. 8 caracteres).');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await invokeReportError({
        tipo: cat.tipo,
        mensaje: body,
        pagina: typeof window !== 'undefined' ? window.location.href : pagina,
        metadata: {
          origen: 'ReportarErrorBtn',
          categoria,
          path: pagina,
          soporte_email: SITE_CONFIG.emailSoporte,
        },
      });
      setMsg(cat.enviado);
      setTexto('');
      setTimeout(() => {
        setOpen(false);
        setMsg('');
      }, 2200);
    } catch (e) {
      setMsg(e?.message || 'No se pudo enviar. Inténtalo más tarde.');
    } finally {
      setBusy(false);
    }
  }, [texto, pagina, cat, categoria]);

  const chipStyle = id => {
    const on = categoria === id;
    return {
      flex: 1,
      padding: '9px 10px',
      borderRadius: 8,
      border: `1px solid ${on ? C.gold : MP_SURF.chipInactiveBorder}`,
      background: on ? 'rgba(200,168,75,0.14)' : MP_SURF.chipInactiveBg,
      color: on ? C.gold : MP_SURF.chipInactiveColor,
      fontWeight: on ? 800 : 600,
      fontSize: 12,
      cursor: busy ? 'default' : 'pointer',
      fontFamily: 'inherit',
    };
  };

  return (
    <>
      <button
        type="button"
        data-guest-safe
        title="Error o sugerencia"
        aria-label="Enviar error o sugerencia"
        onClick={() => {
          setOpen(true);
          setMsg('');
        }}
        style={{
          position: 'fixed',
          left: filtersDrawerOpen ? 'min(calc(400px + 14px), calc(100vw - 58px))' : 16,
          bottom: filtersDrawerOpen ? 'auto' : 100,
          top: filtersDrawerOpen ? 88 : 'auto',
          zIndex: filtersDrawerOpen ? 225 : 1190,
          width: 46,
          height: 46,
          borderRadius: '50%',
          border: '1.5px solid rgba(200,168,75,0.35)',
          background: C.darkNav,
          color: C.gold,
          fontSize: 20,
          fontWeight: 900,
          cursor: 'pointer',
          boxShadow: '0 6px 22px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          transition: 'left 0.22s ease, top 0.22s ease, bottom 0.22s ease',
        }}
      >
        !
      </button>

      {open ? (
        <div
          data-guest-safe
          role="presentation"
          onClick={() => {
            if (!busy) setOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1260,
            background: 'rgba(0,0,0,0.45)',
            display: 'grid',
            placeItems: 'center',
            padding: 14,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reportar-feedback-titulo"
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(480px, 94vw)',
              background: MP_SURF.drawerBg,
              borderRadius: 14,
              border: '1px solid rgba(200,168,75,0.35)',
              boxShadow: '0 22px 70px rgba(0,0,0,0.28)',
              padding: '18px 18px 16px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
              <div>
                <div id="reportar-feedback-titulo" style={{ fontWeight: 900, fontSize: '1.02rem', color: C.gold }}>
                  Error o sugerencia
                </div>
                <div style={{ fontSize: 11, color: MP_SURF.textDim, marginTop: 4 }}>
                  Página: <span style={{ color: MP_SURF.textMuted, fontWeight: 700 }}>{pagina || '—'}</span>
                </div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
                style={{
                  border: '1px solid rgba(200,168,75,0.35)',
                  background: 'rgba(200,168,75,0.08)',
                  borderRadius: 8,
                  padding: '6px 10px',
                  cursor: busy ? 'default' : 'pointer',
                  fontWeight: 800,
                  fontSize: 12,
                  color: C.gold,
                }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {CATEGORIAS.map(c => (
                <button
                  key={c.id}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setCategoria(c.id);
                    setMsg('');
                  }}
                  style={chipStyle(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 11, color: MP_SURF.textMuted, marginBottom: 10, lineHeight: 1.4 }}>
              {cat.ayuda}
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: MP_SURF.textMuted, marginBottom: 6 }}>
              {cat.pregunta}
            </label>
            <textarea
              value={texto}
              onChange={e => setTexto(e.target.value)}
              rows={5}
              placeholder={cat.placeholder}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 10,
                border: MP_SURF.inputBorder,
                background: MP_SURF.inputBg,
                color: MP_SURF.text,
                fontFamily: 'inherit',
                fontSize: 13,
                resize: 'vertical',
                marginBottom: 10,
              }}
              onFocus={e => {
                e.target.style.borderColor = C.gold;
              }}
              onBlur={e => {
                e.target.style.borderColor = 'rgba(255,255,255,0.12)';
              }}
            />

            {msg ? (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  marginBottom: 10,
                  color: msg.includes('enviad') || msg.includes('Enviad') ? C.green : C.warn,
                }}
              >
                {msg}
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
                style={{
                  border: MP_SURF.inputBorder,
                  background: 'transparent',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: busy ? 'default' : 'pointer',
                  color: MP_SURF.textMuted,
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void enviar()}
                style={{
                  border: 'none',
                  background: C.gold,
                  color: '#000',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontWeight: 900,
                  fontSize: 12,
                  cursor: busy ? 'default' : 'pointer',
                  boxShadow: '0 4px 14px rgba(200,168,75,0.35)',
                }}
              >
                {busy ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 10, color: MP_SURF.textDim, lineHeight: 1.35 }}>
              También puedes escribir a {SITE_CONFIG.emailSoporte}. No incluyas contraseñas ni datos bancarios.
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
